import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { showToast } from "../components/utils/toast";

function SnapDeckGroup({
  dividor,
  decks,
  cardData,
  setSelectedDeck,
  setSelectedCard,
  fetchDecks,
  copyDeckCode,
}) {
  const [expanded, setExpanded] = useState(false);
  const [cardGridOpen, setCardGridOpen] = useState(false);
  const [popupDeck, setPopupDeck] = useState(null);
  const [cardBacks, setCardBacks] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const gridRef = useRef(null);
  const [gridCols, setGridCols] = useState(4);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => setIsMobile(mq.matches);
    apply();

    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    axios
      .get(`${API}/api/data/snapCardBacks`)
      .then((res) => setCardBacks(res.data))
      .catch((err) => {
        console.warn(
          "Could not fetch card backs",
          err.response?.status ?? err.message
        );
      });
  }, []);

  useEffect(() => {
    if (!gridRef.current) return;

    const MIN_CARD_PX = 320;
    const GAP_PX = 14;

    const computeCols = (containerWidth) => {
      const cols = Math.floor((containerWidth + GAP_PX) / (MIN_CARD_PX + GAP_PX));
      return Math.max(1, Math.min(4, cols));
    };

    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      if (width > 0) setGridCols(computeCols(width));
    });

    ro.observe(gridRef.current);
    return () => ro.disconnect();
  }, []);

  const cardBackById = useMemo(() => {
    const map = new Map();
    for (const cb of cardBacks) {
      map.set(Number(cb.id), cb);
    }
    return map;
  }, [cardBacks]);

  if (!decks) return null;

  const previewDecks = expanded ? decks : decks.slice(0, 4);

  const openCardGrid = (deck) => {
    setPopupDeck(deck);
    setCardGridOpen(true);
  };

  const closeCardGrid = () => {
    setCardGridOpen(false);
    setPopupDeck(null);
  };

  const handleShare = async (deck) => {
    try {
      const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      await axios.put(`${API}/api/updateSnapDeck/share/${deck.id}`, {
        share: true,
      });

      showToast("Deck shared!");
      if (fetchDecks) fetchDecks();
    } catch (err) {
      console.error("Failed to share deck:", err);
      showToast("Failed to share", "error");
    }
  };

  return (
    <div className="list-container">
      <div
        className="hof-deck-header"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
      >
        <h1 className="hof-title" style={{ textAlign: "left", marginBottom: 0 }}>
          {dividor.dividor || dividor.name || `Dividor ${dividor.id}`}
        </h1>

        <span style={{ color: "#aaa", fontSize: "0.85rem" }}>
          {expanded ? "▼ Show Less" : `▶ Show All (${decks.length})`}
        </span>
      </div>

      <div
        ref={gridRef}
        className="tier-grid"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
        }}
      >
        {previewDecks.map((deck) => {
          const snapCardBackId =
            deck.snapcardbackid ??
            deck.snapCardBackId ??
            deck.snap_card_back_id ??
            null;

          const cardBackRow =
            snapCardBackId != null
              ? cardBackById.get(Number(snapCardBackId))
              : null;

          const cardBackSrc =
            cardBackRow?.cardbackhtml ||
            cardBackRow?.cardBackHTML ||
            cardBackRow?.cardbackHTML ||
            null;

          return (
            <div
              key={deck.id}
              className="hof-deck"
              style={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "16px",
              }}
            >
              <div
                className="hof-deck-header"
                style={{
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="hof-deck-name"
                    title={deck.deckname}
                    style={{
                      paddingLeft: 0,
                      fontSize: "22px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {deck.deckname}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginTop: "6px",
                    }}
                  >
                    <span className="hof-badge">v{deck.versionnum || "-"}</span>
                    <span className="hof-badge">
                      {deck.deckrank || "Unranked"}
                    </span>

                    {typeof deck.share !== "undefined" && (
                      <span className="hof-badge">
                        {deck.share ? "Shared" : "Private"}
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className="hof-card"
                  onClick={() => openCardGrid(deck)}
                  title="Click to view deck cards"
                  style={{
                    width: "110px",
                    height: "140px",
                    cursor: "pointer",
                    margin: 0,
                    transform: "none",
                  }}
                >
                  {cardBackSrc ? (
                    <img
                      src={cardBackSrc}
                      alt="Card Back"
                      className="hof-card-img card-hover-img"
                      style={{
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  ) : (
                    <div
                      className="hof-card-name"
                      style={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "8px",
                        borderTop: "none",
                      }}
                    >
                      No Card Back
                    </div>
                  )}
                </div>
              </div>

              <div className="deck-actions">
                <button className="button" onClick={() => copyDeckCode(deck)}>
                  Copy Code
                </button>

                <button
                  className="button"
                  onClick={() => handleShare(deck)}
                  disabled={deck.share === true}
                  title={deck.share === true ? "Already shared" : "Share this deck"}
                  style={{
                    opacity: deck.share === true ? 0.6 : 1,
                    cursor: deck.share === true ? "not-allowed" : "pointer",
                  }}
                >
                  {deck.share === true ? "Shared" : "Share"}
                </button>

                <button className="button" onClick={() => setSelectedDeck(deck)}>
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {cardGridOpen && popupDeck && (
        <div
          className="modal-overlay"
          onClick={closeCardGrid}
          style={{
            zIndex: 9999,
            alignItems: isMobile ? "flex-end" : "center",
            padding: isMobile ? "0px" : "24px",
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: isMobile ? "100%" : "min(1100px, 96vw)",
              height: isMobile ? "92vh" : "min(86vh, 820px)",
              display: "flex",
              flexDirection: "column",
              padding: 0,
              overflow: "hidden",
              borderRadius: isMobile ? "18px 18px 0 0" : "18px",
            }}
          >
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "rgba(15,15,15,0.95)",
                backdropFilter: "blur(8px)",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              {isMobile && (
                <div
                  style={{
                    height: "18px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "5px",
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.18)",
                    }}
                  />
                </div>
              )}

              <div
                style={{
                  padding: isMobile ? "10px 14px 12px 14px" : "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    className="hof-deck-name"
                    title={popupDeck.deckname}
                    style={{
                      paddingLeft: 0,
                      fontSize: isMobile ? "1.15rem" : "1.35rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {popupDeck.deckname}
                  </div>

                  <div
                    style={{
                      marginTop: "4px",
                      color: "#aaa",
                      fontSize: isMobile ? "0.85rem" : "0.9rem",
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>v{popupDeck.versionnum || "-"}</span>
                    <span style={{ opacity: 0.7 }}>•</span>
                    <span>{popupDeck.deckrank || "Unranked"}</span>
                  </div>
                </div>

                <button
                  className="button"
                  onClick={closeCardGrid}
                  aria-label="Close"
                  style={{
                    width: isMobile ? "44px" : "42px",
                    height: isMobile ? "44px" : "42px",
                    padding: 0,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                padding: isMobile ? "12px 14px 18px 14px" : "16px 18px 20px 18px",
              }}
            >
              <div
                className="hof-card-grid"
                style={{
                  maxWidth: "none",
                  gridTemplateColumns: isMobile
                    ? "repeat(auto-fit, minmax(140px, 1fr))"
                    : "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                {Array.from({ length: 12 }).map((_, i) => {
                  const cardId = popupDeck?.[`card${i + 1}`];
                  const card = cardId != null ? cardData?.[cardId] : null;

                  return card ? (
                    <button
                      key={i}
                      onClick={() => setSelectedCard(card)}
                      title={card.cardname}
                      style={{
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          margin: 0,
                          transform: "none",
                        }}
                      >
                        <img
                          src={card.customhtml || card.cardhtml}
                          alt={card.cardname}
                          className="hof-card-img card-hover-img"
                          style={{
                            aspectRatio: "2 / 3",
                            height: "auto",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    </button>
                  ) : (
                    <div
                      key={i}
                      className="hof-card"
                      style={{
                        margin: 0,
                        transform: "none",
                        aspectRatio: "2 / 3",
                        display: "grid",
                        placeItems: "center",
                        color: "rgba(255,255,255,0.35)",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        borderStyle: "dashed",
                      }}
                    >
                      Empty
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                background: "rgba(12,12,12,0.95)",
                backdropFilter: "blur(8px)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                padding: isMobile ? "12px 14px" : "12px 18px",
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="button"
                onClick={closeCardGrid}
                style={{
                  width: isMobile ? "100%" : "auto",
                  padding: isMobile ? "12px 14px" : undefined,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SnapDeckGroup;